import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button } from '@insurancemenu/imenu-components-lib';
import { ArrowBack } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import DisclaimerComponent from 'components/common/general/DisclaimerComponent';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { toJS } from 'mobx';
import { ProposalConfirmationStatus } from 'store/ui-stores/proposal-v2/proposalItemsStore';
import { EnrollmentType } from 'types/enrollmentService';
import { RenewalAction } from 'types/domainServiceV2';
import { redirectToRoute } from 'routes/redirectToRoute';
import { RouteV2Id } from 'routes/routesConfigV2';
import { createStyles } from './styles';

const ProposalPageHeader = observer((): JSX.Element => {
  // Common data
  const history = useHistory();
  const { periodId } = useParams<{ periodId: string }>();

  // Mobx store
  const {
    proposalItemsStore: {
      proposalConfirmationStatus,
      enrollmentPeriod,
      otherEnrollmentIsActiveForThisGroup,
      goBackUrl,
      group,
      editProposal,
      copyCartAndSaveProposal,
    },
    domainStoreV2: { domainHeader, groupInitiationRedirect },
  } = useRootStore();

  const groupId = group.id;

  const renewalActions = domainHeader.properties.availableRenewalActions?.map((action) => toJS(action)).slice() || [];
  const isRenewalChangePlanAvailable = (): boolean => {
    return enrollmentPeriod.type !== EnrollmentType.RENEWAL || renewalActions.includes(RenewalAction.CHANGE_PLAN);
  };

  // Component actions
  const onBackRedirect = (): void => {
    history.push(goBackUrl);
  };

  const copyProposal = async (): Promise<void> => {
    if (groupInitiationRedirect) {
      const { cartId: newCartId, periodId: newPeriodId } = await copyCartAndSaveProposal();
      redirectToRoute(RouteV2Id.QUOTE_CHECKLIST, { groupId, periodId: newPeriodId, cartId: newCartId });
    } else {
      await editProposal(periodId);
    }
  };

  const classes = createStyles();

  const createBasedOnCurrent = (): JSX.Element => {
    return isRenewalChangePlanAvailable() ? (
      <DisclaimerComponent
        title="Need to make changes?"
        text="You can switch, add, or remove a plan in your proposal below. If the plan and/or product line is not in this proposal, you need to create a new proposal and confirm that proposal."
        actionButtonText="New Proposal based on current"
        onAction={(): void => {
          void copyProposal();
        }}
      />
    ) : (
      <></>
    );
  };

  // concomitant layout
  const HEADER_LAYOUT = {
    [ProposalConfirmationStatus.NEW]: (
      <>
        <h1 className={classes.title}>Review & Confirm Proposal</h1>
        <h2 className={classes.subTitle}>Select the final products for this client</h2>
      </>
    ),
    [ProposalConfirmationStatus.CONFIRMED]: (
      <>
        <h1 className={classes.title}>Confirmed Proposal</h1>
        {createBasedOnCurrent()}
      </>
    ),
    [ProposalConfirmationStatus.SWITCH]: (
      <>
        <h1 className={classes.title}>Switch Proposal</h1>
        <h2 className={classes.subTitle}>
          {`You already have a confirmed proposal for ${dateFormatters.normDateToShortAmericanDate(
            enrollmentPeriod.effectiveDate,
          )}. Switching proposals may result in lost enrollment progress.`}
        </h2>
      </>
    ),
    [ProposalConfirmationStatus.SWAP]: (
      <>
        <h1 className={classes.title}>Alternate Proposal</h1>
        {otherEnrollmentIsActiveForThisGroup && (
          <h2 className={classes.subTitle}>
            {`You  already have a confirmed proposal for ${dateFormatters.normDateToShortAmericanDate(
              otherEnrollmentIsActiveForThisGroup.effectiveDate,
            )}. Swapping to this alternate proposal will result in the loss of any enrollment progress so far to ensure compliance with carrier rules.`}
          </h2>
        )}
      </>
    ),
    [ProposalConfirmationStatus.SUBMITTED]: (
      <>
        <h1 className={classes.title}>Proposal</h1>
        <h2 className={classes.subTitle}>
          {`Attention! You have already submitted a proposal for the effective date, ${dateFormatters.normDateToShortAmericanDate(
            enrollmentPeriod.effectiveDate,
          )} - we recommend notifying the carrier if you resubmit.`}
        </h2>
        <br />
        {createBasedOnCurrent()}
      </>
    ),
    [ProposalConfirmationStatus.ARCHIVED]: <h1 className={classes.title}>Proposal</h1>,
    [ProposalConfirmationStatus.SOLD]: <h1 className={classes.title}>Proposal</h1>,
  };

  return (
    <header className={classes.header}>
      <Button
        type="text"
        size="medium"
        onClick={onBackRedirect}
        leftIconProp={<ArrowBack />}
        className={classes.backButton}
      >
        Back
      </Button>
      {HEADER_LAYOUT[proposalConfirmationStatus]}
    </header>
  );
});

export default ProposalPageHeader;
